import { getServiceErrorMessage } from './utilities';
import { configService } from './configService';
import { secureService } from './secureService';

/**
 * Gets the collection of apps with optional filtering
 * @param {object} options - query options (publisherId, status, searchQuery, includeManifest, continuationToken, pageSize)
 * @returns {Promise<object>|error} a resolved promise response that contains a collection of apps
 */
export async function getApps(options = {}) {
    const { publisherId, status, searchQuery, includeManifest, continuationToken, pageSize } = options;
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps`;
    const params = {};

    if (publisherId) params.publisherId = publisherId;
    if (status) params.status = status;
    if (searchQuery) params.searchQuery = searchQuery;
    if (includeManifest !== undefined) params.includeManifest = includeManifest;
    if (continuationToken) params.continuationToken = continuationToken;
    if (pageSize) params.pageSize = pageSize;

    const response = await secureService.get(url, { params });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return {
        items: response.data.items || [],
        continuationToken: response.data.continuationToken,
        hasAnotherPage: response.data.hasAnotherPage || false,
    };
}

/**
 * Gets a single app by ID
 * @param {string} appId - the app ID
 * @returns {Promise<object>|error} a resolved promise response that contains the app details
 */
export async function getApp(appId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}`;
    const response = await secureService.get(url);

    if (response.data?.status !== 200 || !response.data?.item) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Registers a new app
 * @param {object} manifest - the app manifest
 * @returns {Promise<object>|error} a resolved promise response that contains the new app
 */
export async function registerApp(manifest) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps`;

    // Stringify configurationSchema if it's an object
    const processedManifest = { ...manifest };
    if (processedManifest.configurationSchema && typeof processedManifest.configurationSchema === 'object') {
        processedManifest.configurationSchema = JSON.stringify(processedManifest.configurationSchema);
    }

    // Backend expects RegisterAppRequest with publisherId, appName, and manifest
    const payload = {
        publisherId: manifest.publisherId,
        appName: manifest.appName,
        manifest: processedManifest
    };

    const response = await secureService.post(url, payload);

    if (response.data?.status !== 200 || !response.data?.item) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Updates an existing app
 * @param {string} appId - the app ID
 * @param {object} manifest - the updated app manifest
 * @returns {Promise<object>|error} a resolved promise response that contains the updated app
 */
export async function updateApp(appId, manifest) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}`;

    // Stringify configurationSchema if it's an object
    const processedManifest = { ...manifest };
    if (processedManifest.configurationSchema && typeof processedManifest.configurationSchema === 'object') {
        processedManifest.configurationSchema = JSON.stringify(processedManifest.configurationSchema);
    }

    const payload = {
        appId,
        manifest: processedManifest
    };

    const response = await secureService.put(url, payload);

    if (response.data?.status !== 200 || !response.data?.item) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Publishes an app
 * @param {string} appId - the app ID
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function publishApp(appId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/publish`;
    const response = await secureService.post(url, { appId });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Unpublishes an app
 * @param {string} appId - the app ID
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function unpublishApp(appId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/unpublish`;
    const response = await secureService.post(url, { appId });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Sets whether an app is subject to API operation metering
 * @param {string} appId - the app ID
 * @param {boolean} enabled - whether API operation metering is enabled for the app
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function setApiOperationMetering(appId, enabled) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/metering`;
    const response = await secureService.post(url, { appId, enabled });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Submits a version for review
 * @param {string} appId - the app ID
 * @param {string} versionId - the version ID to submit for review
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function submitAppForReview(appId, versionId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/reviews/${appId}/submit`;
    const response = await secureService.post(url, { appId, versionId });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Approves a version review
 * @param {string} appId - the app ID
 * @param {string} versionId - the version ID to approve
 * @param {string} comments - optional review comments
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function approveApp(appId, versionId, comments = '') {
    const url = `${configService.adminGatewayUrl}/nexus/admin/reviews/${appId}/approve`;

    const payload = {
        appId,
        versionId,
        comments
    };

    const response = await secureService.post(url, payload);

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Rejects a version review
 * @param {string} appId - the app ID
 * @param {string} versionId - the version ID to reject
 * @param {string} comments - rejection reason/comments
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function rejectApp(appId, versionId, comments) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/reviews/${appId}/reject`;

    const payload = {
        appId,
        versionId,
        comments
    };

    const response = await secureService.post(url, payload);

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Uploads a new version of an app
 * @param {string} appId - the app ID
 * @param {object} manifest - the new version manifest
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function uploadAppVersion(appId, manifest) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/versions`;

    // Stringify configurationSchema if it's an object
    const processedManifest = { ...manifest };
    if (processedManifest.configurationSchema && typeof processedManifest.configurationSchema === 'object') {
        processedManifest.configurationSchema = JSON.stringify(processedManifest.configurationSchema);
    }

    const payload = {
        appId,
        semanticVersion: manifest.version,
        manifest: processedManifest
    };

    const response = await secureService.post(url, payload);

    if (response.data?.status !== 200 || !response.data?.item) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Gets all versions for an app
 * @param {string} appId - the app ID
 * @returns {Promise<Array>|error} a resolved promise response that contains app versions
 */
export async function getAppVersions(appId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/versions`;
    const response = await secureService.get(url);

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data.items || [];
}

/**
 * Deprecates a published version: tenants still on it see "Upgrade required by" the end of support.
 * Deprecating an already deprecated version moves its end of support.
 * @param {string} appId - the app ID
 * @param {string} versionId - the version ID
 * @param {number} endOfSupportSeconds - end of support, in epoch seconds (UTC midnight of the chosen day)
 * @returns {Promise<object>|error} a resolved promise response that contains the version
 */
export async function deprecateAppVersion(appId, versionId, endOfSupportSeconds) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/versions/${versionId}/deprecate`;
    const response = await secureService.post(url, {
        appId,
        versionId,
        endOfSupportTime: { seconds: endOfSupportSeconds }
    });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Clears a version's deprecation and end of support
 * @param {string} appId - the app ID
 * @param {string} versionId - the version ID
 * @returns {Promise<object>|error} a resolved promise response that contains the version
 */
export async function undeprecateAppVersion(appId, versionId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/versions/${versionId}/undeprecate`;
    const response = await secureService.post(url, { appId, versionId });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Gets how many tenants run each version of an app, across the instance
 * @param {string} appId - the app ID
 * @returns {Promise<object>|error} a resolved promise response that contains the installation counts
 */
export async function getAppInstallations(appId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/installations`;
    const response = await secureService.get(url);

    if (response.data?.status !== 200 || !response.data?.item) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Gets the manifest for a specific app version
 * @param {string} appId - the app ID
 * @param {string} versionId - the version ID
 * @returns {Promise<object>|error} a resolved promise response that contains the manifest
 */
export async function getAppManifest(appId, versionId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}/versions/${versionId}/manifest`;
    const response = await secureService.get(url);

    if (response.data?.status !== 200 || !response.data?.item) {
        throw getServiceErrorMessage(response);
    }

    return response.data.item;
}

/**
 * Deletes an app
 * @param {string} appId - the app ID
 * @returns {Promise<object>|error} a resolved promise response
 */
export async function deleteApp(appId) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/${appId}`;
    const response = await secureService.delete(url);

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data;
}

/**
 * Gets all available app categories with localized titles and descriptions
 * @param {number} lcid - Optional LCID for sorting by localized title (default: 1033 - English)
 * @returns {Promise<Array>|error} a resolved promise response that contains category definitions
 */
export async function getCategories(lcid) {
    const url = `${configService.adminGatewayUrl}/nexus/admin/apps/categories`;
    const params = {};

    if (lcid) {
        params.lcid = lcid;
    }

    const response = await secureService.get(url, { params });

    if (response.data?.status !== 200) {
        throw getServiceErrorMessage(response);
    }

    return response.data.items || [];
}
